/* SystemVersionBadge — public trust signal on Landing / Pricing / Kit pages.
   Shows the current published SystemVersion month + opens a popover detailing
   what's in the system this month (changelog + 3 trend previews + AI stack).

   Variants:
     - badge  : compact pill (default for Pricing / Kit)
     - hero   : single line of small text for Landing hero

   2026-10-08 — strings routed through i18n so the hero line / badge / modal
   render in Chinese on /cn.
*/
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, X } from "lucide-react";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SystemVersionBadge({ variant = "badge", className = "" }) {
    const { t } = useT();
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        axios.get(`${API}/system-version-public/current`)
            .then((r) => r.data?.available && setData(r.data))
            .catch(() => {});
    }, []);

    if (!data) return null;

    if (variant === "hero") {
        return (
            <button
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-2 text-xs text-cafe-mocha hover:text-cafe-espresso transition-colors group ${className}`}
                data-testid="sysver-badge-hero"
            >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-cafe-olive animate-pulse" />
                <span>{t("sysver.updated")} · {t("sysver.system_prefix")} {data.label}</span>
                <span className="text-[10px] uppercase tracking-wider opacity-60 group-hover:opacity-100">{t("sysver.whats_new")} →</span>
                <Modal open={open} onClose={() => setOpen(false)} data={data} />
            </button>
        );
    }

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`inline-flex items-center gap-2 bg-cafe-olive/8 hover:bg-cafe-olive/15 border border-cafe-olive/25 rounded-full px-3.5 py-1.5 transition-colors group ${className}`}
                data-testid="sysver-badge"
            >
                <Sparkles className="h-3 w-3 text-cafe-olive" />
                <span className="text-[11px] uppercase tracking-[0.16em] text-cafe-espresso font-bold">
                    {t("sysver.updated")}
                </span>
                <span className="text-[11px] text-cafe-mocha font-mono">{data.label}</span>
            </button>
            <Modal open={open} onClose={() => setOpen(false)} data={data} />
        </>
    );
}

function Modal({ open, onClose, data }) {
    const { t, lang } = useT();
    if (!open) return null;
    const publishedDate = data.published_at ? new Date(data.published_at).toLocaleDateString(lang === "cn" ? "zh-CN" : "en-MY", { year: "numeric", month: "long", day: "numeric" }) : "";
    const ai = data.ai_rendering || {};
    return (
        <div
            className="fixed inset-0 z-[60] bg-cafe-espresso/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200"
            onClick={onClose}
            data-testid="sysver-modal"
        >
            <div
                className="bg-cafe-cream max-w-2xl w-full rounded-2xl p-7 max-h-[85vh] overflow-y-auto relative shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className="absolute top-4 right-4 text-cafe-mocha hover:text-cafe-espresso" data-testid="sysver-modal-close">
                    <X className="h-5 w-5" />
                </button>

                <div className="mb-5">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">{t("sysver.system_name")}</p>
                    <h2 className="font-serif text-3xl mt-1">{data.label}</h2>
                    <p className="text-xs text-cafe-mocha mt-1">{t("sysver.refreshed")} {publishedDate}</p>
                </div>

                {data.changelog && (
                    <div className="mb-6 bg-cafe-olive/8 border-l-4 border-cafe-olive p-4 rounded">
                        <p className="text-[10px] uppercase tracking-wider text-cafe-olive font-bold mb-1">{t("sysver.whats_new_month")}</p>
                        <p className="text-sm text-cafe-espresso leading-relaxed">{data.changelog}</p>
                    </div>
                )}

                {data.preview_trends?.length > 0 && (
                    <div className="mb-6">
                        <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold mb-2">{t("sysver.ig_trends")}</p>
                        <ul className="space-y-3">
                            {data.preview_trends.map((trend, i) => (
                                <li key={i} className="text-sm border-l-2 border-cafe-olive/30 pl-3">
                                    <p className="font-bold text-cafe-espresso">{trend.trend}</p>
                                    <p className="text-cafe-mocha text-xs italic mt-0.5">{trend.why_it_works}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3 text-[11px] text-cafe-mocha">
                    {ai.text_model && <div><span className="uppercase tracking-wider text-cafe-olive font-bold block">{t("sysver.ai.text")}</span> {ai.text_model}</div>}
                    {ai.image_model && <div><span className="uppercase tracking-wider text-cafe-olive font-bold block">{t("sysver.ai.image")}</span> {ai.image_model}</div>}
                    {ai.video_model && <div><span className="uppercase tracking-wider text-cafe-olive font-bold block">{t("sysver.ai.video")}</span> {ai.video_model}</div>}
                    {ai.vision_model && <div><span className="uppercase tracking-wider text-cafe-olive font-bold block">{t("sysver.ai.vision")}</span> {ai.vision_model}</div>}
                </div>

                <p className="text-[11px] text-cafe-mocha italic mt-6 pt-4 border-t border-border">
                    {t("sysver.summary_prefix")}{data.trend_count}{t("sysver.summary_trends")}{data.hook_count}{t("sysver.summary_hooks")}{data.visual_styles_count}{t("sysver.summary_styles")}
                </p>
            </div>
        </div>
    );
}
